<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { PlayerAward } from '../types';
  import PhotoSelectionDialog from './PhotoSelectionDialog.svelte';
  import { createEventDispatcher, onMount } from 'svelte';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../lib/utils/dialogAnimations';

  export let award;
  export let playerAward: PlayerAward | null = null;
  export let matchId;
  export let teamMembers: any[] = [];
  export let sport: string;

  // Create reactive variable with defaults when playerAward is null
  let awardData = playerAward || {
    id: award?.id,
    user_id: '',
    name: '',
    comments: '',
    award_photo_type: 'default',
    award_photo_filename: 'background_1.jpg',
  };

  const dispatch = createEventDispatcher();

  let photoDialogOpen = false;
  let dialogElement: HTMLDialogElement;
  
  function handlePlayerChange(event) {
    const selectedId = event.target.value;
    
    if (selectedId) {
      // Find the selected team member
      const member = teamMembers.find(m => {
        // Handle both string IDs and object members
        if (typeof m === 'string') {
          return m === selectedId;
        } else {
          return m.id === selectedId;
        }
      });
      
      // Update player info
      if (member) {
        if (typeof member === 'string') {
          awardData.user_id = member;
          awardData.name = member;
        } else {
          awardData.user_id = member.id;
          awardData.name = member.name;
        }
      }
    }
  }

  async function handleAwardSubmit() {
    if (!awardData.user_id) {
      // Don't save if no player is selected
      return;
    }

    await closeDialogWithSlideDown(dialogElement);
    dispatch('save', awardData);
  }

  function openPhotoDialog() {
    photoDialogOpen = true;
  }

  function closePhotoDialog() {
    photoDialogOpen = false;
  }

  // Handle photo selection
  function handlePhotoSelection(event) {
    const { file, filename, url, type } = event.detail;

    console.log('handlePhotoSelection', event.detail);
    
    awardData.award_photo_type = type;    
    awardData.award_photo_filename = filename;
    
    photoDialogOpen = false;
  }

  async function handleClose() {
    await closeDialogWithSlideDown(dialogElement);
    dispatch('close');
  }

  async function handleDelete() {
    await closeDialogWithSlideDown(dialogElement);
    dispatch('delete', award.id);
  }

  onMount(async () => {
    if (dialogElement) {
      await openDialogWithSlideUp(dialogElement);
    }
  });
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<dialog
  bind:this={dialogElement}
  on:click={handleClose}
  on:keydown={(e) => e.key === 'Escape' && handleClose()}
  aria-labelledby="modal-title"
  class="slide-up-dialog"
>
  <article class="mt-0">
    <header class="py-4">
        <h5>{award.icon} {award.label}</h5>
        <button class="close-button" on:click={handleClose}>×</button>
    </header>
    
    <!-- Scrollable Content -->
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div on:click={(event) => event.stopPropagation()}>
        <div class="player-selection">
            <select id="player-select" bind:value={awardData.user_id} on:change={handlePlayerChange}>
            <option value="">{$_('playerAward.selectPlayer')}</option>
            {#each teamMembers as member}
                {@const memberId = typeof member === 'string' ? member : member.id}
                {@const memberName = typeof member === 'string' ? member : member.name}
                <option value={memberId}>{memberName}</option>
            {/each}
            </select>
        </div>
      
        <div class="award-photo-section">
            <button class="outline full-width" on:click={openPhotoDialog}>
            {$_('playerAward.chooseImage')}
            </button>
            {#if awardData.award_photo_filename}
            <div class="photo-preview-container">
                <div class="photo-preview">
                    {#if awardData.award_photo_url}
                        <img src={awardData.award_photo_url} alt="Preview" />
                    {:else if awardData.award_photo_filename.includes('/')}
                        <img src={`/uploads/${awardData.award_photo_filename}`} alt="Preview" />
                    {:else}
                        <img src={`/img/backgrounds/award/${sport}/${awardData.award_photo_filename}`} alt="Preview" />
                    {/if}
                </div>
                <div class="file-name">
                    {awardData.award_photo_filename.includes('/') ? awardData.award_photo_filename.split('/').pop() : awardData.award_photo_filename}
                </div>
            </div>
            {:else}
            <div class="text text-sm mt-2">{$_('playerAward.noPhoto')}</div>
            {/if}
        </div>

        <textarea 
            id="award-comment"
            bind:value={awardData.comments}
            placeholder={$_('playerAward.justificationPlaceholder')}
            rows="4"
        ></textarea>
        <p class="text text-italic text-sm mt-2">{$_('playerAward.instructions')}</p>

        <footer class="">
            <div class="flex space-between full-width">
              {#if awardData?.id}
                <button type="button" class="secondary outline ghost small" on:click={handleDelete}>
                <ion-icon name="trash-outline"></ion-icon>
                {$_('common.delete')}
                </button>
              {/if}
              <button type="button" class="primary" on:click={handleAwardSubmit} disabled={!awardData.user_id}>
              {$_('common.save')}
              </button>
            </div>            


        </footer>

    </div>
  </article>
</dialog>

{#if photoDialogOpen}
    <PhotoSelectionDialog 
      sport={sport}
      photoType="award"
      matchId={matchId}
      on:close={closePhotoDialog}
      on:select={handlePhotoSelection}
    />
{/if}

<style>
  .player-selection {
    margin-bottom: 1rem;
  }

  .award-photo-section {
    margin-bottom: 1.5rem;
  }

  .photo-preview-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    margin-bottom: 1rem;
  }

  .photo-preview {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .photo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .file-name {
    font-size: 0.875rem;
    color: var(--text-muted);
    flex: 1;
    word-break: break-word;
  }


</style> 